import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import { categories, mainSiteUrl } from '@/mocks/categories';

const languages = [
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'pl', label: 'Polski', flag: '🇵🇱' },
  { code: 'es', label: 'Español', flag: '🇪🇸' },
  { code: 'fr', label: 'Français', flag: '🇫🇷' },
];

export default function Navbar() {
  const { t } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [catOpen, setCatOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const currentLang = languages.find((l) => l.code === (i18n.language || 'en')) || languages[0];

  const changeLanguage = (code: string) => {
    i18n.changeLanguage(code);
    setLangOpen(false);
  };

  const navLinks = [
    { label: t('navHome'), href: '/', internal: true },
    { label: t('navSpreadsheet'), href: '/usfans-spreadsheet-guide', internal: true },
    { label: t('navGuides'), href: '/#featured', internal: true },
    { label: t('navReviews'), href: '/#review', internal: true },
    { label: t('navShipping'), href: '/#resources', internal: true },
    { label: t('navSafety'), href: '/#safety', internal: true },
    { label: t('navShoppingGuide'), href: mainSiteUrl, external: true },
  ];

  const linkClass = scrolled
    ? 'text-foreground-700 hover:text-foreground-950'
    : 'text-background-100 hover:text-background-50';

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-background-50/95 backdrop-blur border-b border-background-200' : 'bg-transparent'
      }`}
    >
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="flex h-16 items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2 whitespace-nowrap" onClick={() => setMobileOpen(false)}>
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 text-background-50">
              <i className="ri-leaf-line text-lg"></i>
            </span>
            <span className={`text-lg font-bold font-heading ${scrolled ? 'text-foreground-950' : 'text-background-50'}`}>
              {t('brand')}
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            <div className="relative">
              <button
                onClick={() => {
                  setCatOpen((v) => !v);
                  setLangOpen(false);
                }}
                className={`flex items-center gap-1.5 rounded-md px-3 py-2 text-sm font-medium whitespace-nowrap cursor-pointer ${linkClass}`}
              >
                <span>{t('browseLabel')}</span>
                <i className={`ri-arrow-down-s-line text-base transition-transform ${catOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {catOpen && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setCatOpen(false)}></div>
                  <div className="absolute left-0 top-full z-20 mt-2 w-[560px] rounded-lg border border-background-200 bg-background-50 p-2 shadow-lg">
                    <div className="grid grid-cols-2 gap-1 max-h-[440px] overflow-y-auto">
                      {categories.map((c) => (
                        <a
                          key={c.name}
                          href={c.url}
                          target="_blank"
                          rel="noopener nofollow"
                          className="flex items-center gap-2.5 rounded-md px-2.5 py-2 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-950 cursor-pointer"
                        >
                          <span className="h-9 w-12 flex-shrink-0 overflow-hidden rounded-md bg-background-100">
                            <img src={c.image} alt={c.name} className="h-full w-full object-cover object-top" />
                          </span>
                          <span className="whitespace-nowrap">{c.name}</span>
                        </a>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>

            {navLinks.map((link) => {
              if (link.internal) {
                return (
                  <Link
                    key={link.label}
                    to={link.href}
                    className={`rounded-md px-3 py-2 text-sm font-medium whitespace-nowrap ${linkClass}`}
                  >
                    {link.label}
                  </Link>
                );
              }
              if (link.external) {
                return (
                  <a
                    key={link.label}
                    href={link.href}
                    target="_blank"
                    rel="noopener nofollow"
                    className={`rounded-md px-3 py-2 text-sm font-medium whitespace-nowrap ${linkClass}`}
                  >
                    {link.label}
                  </a>
                );
              }
              return (
                <a
                  key={link.label}
                  href={link.href}
                  className={`rounded-md px-3 py-2 text-sm font-medium whitespace-nowrap ${linkClass}`}
                >
                  {link.label}
                </a>
              );
            })}
          </nav>

          <div className="flex items-center gap-2">
            <div className="relative">
              <button
                onClick={() => {
                  setLangOpen((v) => !v);
                  setCatOpen(false);
                }}
                className={`flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm font-medium whitespace-nowrap cursor-pointer ${
                  scrolled
                    ? 'border-background-200 text-foreground-700 hover:bg-background-100'
                    : 'border-background-50/30 text-background-50 hover:bg-background-50/10'
                }`}
              >
                <span>{currentLang.flag}</span>
                <span>{currentLang.code.toUpperCase()}</span>
                <i className={`ri-arrow-down-s-line text-base transition-transform ${langOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {langOpen && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setLangOpen(false)}></div>
                  <div className="absolute right-0 top-full z-20 mt-2 w-44 rounded-lg border border-background-200 bg-background-50 p-1.5 shadow-lg">
                    {languages.map((l) => (
                      <button
                        key={l.code}
                        onClick={() => changeLanguage(l.code)}
                        className={`flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm whitespace-nowrap cursor-pointer ${
                          l.code === currentLang.code
                            ? 'bg-primary-100 text-primary-700 font-semibold'
                            : 'text-foreground-700 hover:bg-background-100'
                        }`}
                      >
                        <span>{l.flag}</span>
                        <span>{l.label}</span>
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>

            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="hidden md:inline-flex items-center gap-1.5 rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 hover:bg-primary-600 whitespace-nowrap cursor-pointer"
            >
              {t('buyNow')}
            </a>

            <button
              onClick={() => setMobileOpen((v) => !v)}
              className={`lg:hidden flex h-10 w-10 items-center justify-center rounded-md cursor-pointer ${
                scrolled ? 'text-foreground-700 hover:bg-background-100' : 'text-background-50 hover:bg-background-50/10'
              }`}
              aria-label="Menu"
            >
              <i className={`${mobileOpen ? 'ri-close-line' : 'ri-menu-line'} text-xl`}></i>
            </button>
          </div>
        </div>
      </div>

      {mobileOpen && (
        <div className="lg:hidden border-t border-background-200 bg-background-50 px-4 py-4 max-h-[75vh] overflow-y-auto">
          <nav className="flex flex-col gap-1">
            {navLinks.map((link) => {
              if (link.internal) {
                return (
                  <Link
                    key={link.label}
                    to={link.href}
                    onClick={() => setMobileOpen(false)}
                    className="rounded-md px-3 py-2.5 text-sm font-medium text-foreground-700 hover:bg-background-100"
                  >
                    {link.label}
                  </Link>
                );
              }
              if (link.external) {
                return (
                  <a
                    key={link.label}
                    href={link.href}
                    target="_blank"
                    rel="noopener nofollow"
                    onClick={() => setMobileOpen(false)}
                    className="rounded-md px-3 py-2.5 text-sm font-medium text-foreground-700 hover:bg-background-100"
                  >
                    {link.label}
                  </a>
                );
              }
              return (
                <a
                  key={link.label}
                  href={link.href}
                  onClick={() => setMobileOpen(false)}
                  className="rounded-md px-3 py-2.5 text-sm font-medium text-foreground-700 hover:bg-background-100"
                >
                  {link.label}
                </a>
              );
            })}
            <div className="my-2 border-t border-background-200"></div>
            <p className="px-3 pb-1 text-xs font-semibold text-foreground-500 uppercase">{t('browseLabel')}</p>
            <div className="grid grid-cols-2 gap-1.5">
              {categories.map((c) => (
                <a
                  key={c.name}
                  href={c.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center gap-2 rounded-md border border-background-200 bg-background-50 px-2 py-2 text-sm text-foreground-700 hover:bg-background-100"
                >
                  <span className="h-8 w-10 flex-shrink-0 overflow-hidden rounded-md bg-background-100">
                    <img src={c.image} alt={c.name} className="h-full w-full object-cover object-top" />
                  </span>
                  <span className="truncate">{c.name}</span>
                </a>
              ))}
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}